import { useEffect, useState, useCallback } from 'react'
import { authedFetch } from '../api'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../hooks'
import { deleteFailure } from '../slices/failuresSlice'
import SidebarNavigation from '../components/SidebarNavigation'
import FailureDetailHeader from '../components/FailureDetailHeader'
import FailureDetailMeta from '../components/FailureDetailMeta'
import FailureDetailSections from '../components/FailureDetailSections'
import FailureDetailResults from '../components/FailureDetailResults'
import '../components/WebhookEventsFeed.css'
import './FailureDetailPage.css'

interface FailureDetailResult {
  agent_name: string
  status: 'pass' | 'fail' | 'dsq' | 'disagree'
  cost: number
  time: string
  run_id: string
  run_name: string
}

interface CleanEvent {
  id: string
  type: string
  container_name: string
  timestamp: string
  data: string
}

interface AttemptEvents {
  containerName: string
  attemptNumber: number
  events: CleanEvent[]
}

interface CleanAttempt {
  id: string
  container_name: string
  attempt_number: number
  status: string
  error: string
  created_at: string
  completed_at: string | null
}

interface RefineAttempt {
  id: string
  container_name: string
  attempt_number: number
  status: string
  error: string
  created_at: string
  completed_at: string | null
}

interface FailureRunResult {
  task_id: string
  status: string
  llm_assessment: string
  dsq_claude_result: string
  dsq_claude: string
  dsq_codex_result: string
  dsq_codex: string
  grade_claude_result: string
  grade_claude: string
  grade_codex_result: string
  grade_codex: string
  root_cause_judge_result: string
  root_cause_judge: string
  cost: number
  time_seconds: number
  attempt: number
  assessed_at: string | null
  agent_name: string
  run_id: string
  run_name: string
}

interface FailureDetail {
  id: string
  title: string
  tag_name: string | null
  tag_names: string[]
  github_repository: string
  branch: string
  failing_changeset: string
  fixed_changeset: string
  test_file: string
  clean_branch: string
  clean_status: string
  clean_error: string
  clean_attempts: CleanAttempt[]
  refined_branch: string
  refined_status: string
  refined_error: string
  refined_recording_id: string
  refined_playwright_recording_id: string
  refined_error_message: string
  refined_backend_recordings: string
  refine_script_logs: string
  refine_attempts: RefineAttempt[]
  root_cause_status: string
  root_cause_error: string
  root_cause_locations: string
  root_cause_explanation: string
  root_cause_proof: string
  root_cause_judge_claude: string
  root_cause_judge_codex: string
  test_archive_url: string
  results_archive_url: string
  root_cause_attempts: Array<{ id: string; container_name: string; attempt_number: number; status: string; error: string; created_at: string; completed_at: string | null }>
  run_status: string
  run_error: string
  summarize_status: string
  summarize_error: string
  summarize_claude: string
  summarize_codex: string
  failure_run_results: FailureRunResult[]
  short_id: string
  created_at: string
  latest_results: FailureDetailResult[]
  last_run_date: string | null
}

function FailureDetailPage() {
  const { failureId } = useParams<{ failureId: string }>()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [failure, setFailure] = useState<FailureDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cleanAttemptEvents, setCleanAttemptEvents] = useState<AttemptEvents[]>([])
  const [expandedAttempts, setExpandedAttempts] = useState<Set<number>>(new Set())
  const [cleanEventsLoading] = useState(false)
  const [refineAttemptEvents, setRefineAttemptEvents] = useState<AttemptEvents[]>([])
  const [expandedRefineAttempts, setExpandedRefineAttempts] = useState<Set<number>>(new Set())
  const [refineEventsLoading] = useState(false)
  const [rootCauseAttemptEvents, setRootCauseAttemptEvents] = useState<AttemptEvents[]>([])
  const [expandedRootCauseAttempts, setExpandedRootCauseAttempts] = useState<Set<number>>(new Set())
  const [rootCauseEventsLoading] = useState(false)
  const [refreshingStage, setRefreshingStage] = useState<string | null>(null)

  const fetchFailureData = useCallback(() => {
    if (!failureId) return
    return fetch(`/api/failures/${failureId}`)
      .then((res) => {
        if (res.status === 404) return null
        if (!res.ok) throw new Error('Failed to fetch failure')
        return res.json() as Promise<FailureDetail>
      })
      .then((data) => {
        if (data) setFailure(data)
      })
  }, [failureId])

  useEffect(() => {
    if (!failureId) return
    setLoading(true)
    fetchFailureData()
      ?.catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [failureId, fetchFailureData])

  const fetchAllCleanEvents = useCallback(async (attempts: Array<{ container_name: string; attempt_number: number; created_at: string; completed_at: string | null }>) => {
    if (attempts.length === 0) return
    const results: AttemptEvents[] = []
    for (const attempt of attempts) {
      try {
        const since = encodeURIComponent(attempt.created_at)
        const untilParam = attempt.completed_at ? `&until=${encodeURIComponent(attempt.completed_at)}` : ''
        const res = await fetch(`/api/webhook-events?container_name=${encodeURIComponent(attempt.container_name)}&since=${since}${untilParam}&limit=5000`)
        if (res.ok) {
          const events = await res.json() as CleanEvent[]
          if (events.length > 0) {
            results.push({ containerName: attempt.container_name, attemptNumber: attempt.attempt_number, events })
          }
        }
      } catch {
        // silently fail for individual containers
      }
    }
    setCleanAttemptEvents(results)
  }, [])

  const fetchAllRefineEvents = useCallback(async (attempts: Array<{ container_name: string; attempt_number: number; created_at: string; completed_at: string | null }>) => {
    if (attempts.length === 0) return
    const results: AttemptEvents[] = []
    for (const attempt of attempts) {
      try {
        const since = encodeURIComponent(attempt.created_at)
        const untilParam = attempt.completed_at ? `&until=${encodeURIComponent(attempt.completed_at)}` : ''
        const res = await fetch(`/api/webhook-events?container_name=${encodeURIComponent(attempt.container_name)}&since=${since}${untilParam}&limit=5000`)
        if (res.ok) {
          const events = await res.json() as CleanEvent[]
          if (events.length > 0) {
            results.push({ containerName: attempt.container_name, attemptNumber: attempt.attempt_number, events })
          }
        }
      } catch {
        // silently fail for individual containers
      }
    }
    setRefineAttemptEvents(results)
  }, [])

  const fetchAllRootCauseEvents = useCallback(async (attempts: Array<{ container_name: string; attempt_number: number; created_at: string; completed_at: string | null }>) => {
    if (attempts.length === 0) return
    const results: AttemptEvents[] = []
    for (const attempt of attempts) {
      try {
        const since = encodeURIComponent(attempt.created_at)
        const untilParam = attempt.completed_at ? `&until=${encodeURIComponent(attempt.completed_at)}` : ''
        const res = await fetch(`/api/webhook-events?container_name=${encodeURIComponent(attempt.container_name)}&since=${since}${untilParam}&limit=5000`)
        if (res.ok) {
          const events = await res.json() as CleanEvent[]
          if (events.length > 0) {
            results.push({ containerName: attempt.container_name, attemptNumber: attempt.attempt_number, events })
          }
        }
      } catch {
        // silently fail for individual containers
      }
    }
    setRootCauseAttemptEvents(results)
  }, [])

  // Auto-fetch clean events when failure data loads and has clean attempts
  useEffect(() => {
    if (!failure) return
    const attempts = failure.clean_attempts || []
    if (attempts.length > 0) {
      fetchAllCleanEvents(attempts)
    } else {
      setCleanAttemptEvents([])
    }
  }, [failure?.clean_attempts?.length, fetchAllCleanEvents])

  // Auto-fetch refine events when failure data loads and has refine attempts
  useEffect(() => {
    if (!failure) return
    const attempts = failure.refine_attempts || []
    if (attempts.length > 0) {
      fetchAllRefineEvents(attempts)
    } else {
      setRefineAttemptEvents([])
    }
  }, [failure?.refine_attempts?.length, fetchAllRefineEvents])

  // Auto-fetch root cause events when failure data loads and has root cause attempts
  useEffect(() => {
    if (!failure) return
    const attempts = failure.root_cause_attempts || []
    if (attempts.length > 0) {
      fetchAllRootCauseEvents(attempts)
    } else {
      setRootCauseAttemptEvents([])
    }
  }, [failure?.root_cause_attempts?.length, fetchAllRootCauseEvents])

  // Poll for updates when stages are active or attempts are expanded
  useEffect(() => {
    if (!failure) return
    const cleanActive = failure.clean_status === 'running'
    const refineActive = failure.refined_status === 'running'
    const rootCauseActive = failure.root_cause_status === 'running'
    const runActive = failure.run_status === 'running'
    const summarizeActive = failure.summarize_status === 'running'
    const hasExpandedClean = expandedAttempts.size > 0
    const hasExpandedRefine = expandedRefineAttempts.size > 0
    const hasExpandedRootCause = expandedRootCauseAttempts.size > 0
    const shouldPoll = cleanActive || refineActive || rootCauseActive || runActive || summarizeActive || hasExpandedClean || hasExpandedRefine || hasExpandedRootCause
    if (!shouldPoll) return

    const interval = setInterval(() => {
      fetchFailureData()
      if (hasExpandedClean) {
        const attempts = failure.clean_attempts || []
        if (attempts.length > 0) fetchAllCleanEvents(attempts)
      }
      if (hasExpandedRefine) {
        const attempts = failure.refine_attempts || []
        if (attempts.length > 0) fetchAllRefineEvents(attempts)
      }
      if (hasExpandedRootCause) {
        const attempts = failure.root_cause_attempts || []
        if (attempts.length > 0) fetchAllRootCauseEvents(attempts)
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [failure?.clean_status, failure?.refined_status, failure?.root_cause_status, failure?.clean_attempts, failure?.refine_attempts, failure?.root_cause_attempts, expandedAttempts.size, expandedRefineAttempts.size, expandedRootCauseAttempts.size, fetchFailureData, fetchAllCleanEvents, fetchAllRefineEvents, fetchAllRootCauseEvents])

  const handleDelete = useCallback(() => {
    if (!failureId) return
    dispatch(deleteFailure(failureId))
      .then(() => {
        navigate('/failures')
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err))
      })
  }, [dispatch, failureId, navigate])

  const handleSaveShortId = useCallback(async (shortIdInput: string) => {
    if (!failureId) return
    const res = await authedFetch(`/api/failures/${failureId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ short_id: shortIdInput }),
    })
    if (!res.ok) throw new Error('Failed to update short ID')
    setFailure((prev) => prev ? { ...prev, short_id: shortIdInput } : prev)
  }, [failureId])

  const handleSaveTags = useCallback(async (tagNames: string[]) => {
    if (!failureId) return
    const res = await authedFetch(`/api/failures/${failureId}/tags`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag_names: tagNames }),
    })
    if (!res.ok) throw new Error('Failed to update tags')
    const data = await res.json() as { tag_names: string[] }
    setFailure((prev) => prev ? { ...prev, tag_names: data.tag_names } : prev)
  }, [failureId])

  const handleToggleCleanEvents = useCallback((attemptIndex: number) => {
    setExpandedAttempts((prev) => {
      const next = new Set(prev)
      if (next.has(attemptIndex)) {
        next.delete(attemptIndex)
      } else {
        next.add(attemptIndex)
      }
      return next
    })
  }, [])

  const handleToggleRefineEvents = useCallback((attemptIndex: number) => {
    setExpandedRefineAttempts((prev) => {
      const next = new Set(prev)
      if (next.has(attemptIndex)) {
        next.delete(attemptIndex)
      } else {
        next.add(attemptIndex)
      }
      return next
    })
  }, [])

  const handleToggleRootCauseEvents = useCallback((attemptIndex: number) => {
    setExpandedRootCauseAttempts((prev) => {
      const next = new Set(prev)
      if (next.has(attemptIndex)) {
        next.delete(attemptIndex)
      } else {
        next.add(attemptIndex)
      }
      return next
    })
  }, [])

  const handleRefreshStage = useCallback(async (stage: string) => {
    if (!failureId) return
    setRefreshingStage(stage)
    try {
      const res = await authedFetch('/api/reset-failure-stage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ failure_id: failureId, stage }),
      })
      if (!res.ok) throw new Error(`Failed to reset ${stage} stage`)

      // Trigger container spawning for stages that use workers
      if (['clean', 'refine', 'root_cause'].includes(stage)) {
        const spawnRes = await authedFetch('/api/trigger-stage', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ failure_id: failureId, stage }),
        })
        if (!spawnRes.ok) {
          const errBody = await spawnRes.json().catch(() => ({}))
          throw new Error((errBody as { error?: string }).error || `Failed to trigger ${stage} stage`)
        }
      }

      await fetchFailureData()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setRefreshingStage(null)
    }
  }, [failureId, fetchFailureData])

  return (
    <div data-testid="failure-detail-page" className="failure-detail-page">
      <SidebarNavigation />

      <main className="failure-detail-main app-page-main" aria-live="polite">
        {loading && (
          <div data-testid="failure-detail-loading" className="failure-detail-loading" role="status">
            Loading failure details...
          </div>
        )}

        {error && (
          <div data-testid="failure-detail-error" className="failure-detail-error" role="alert">
            {error}
          </div>
        )}

        {!loading && !error && !failure && (
          <div data-testid="failure-detail-not-found" className="failure-detail-not-found">
            Failure not found.
          </div>
        )}

        {!loading && failure && (
          <>
            <header className="app-page-sticky-header">
              <div className="app-page-sticky-header-inner">
                <FailureDetailHeader
                  failure={failure}
                  onDelete={handleDelete}
                  onSaveShortId={handleSaveShortId}
                  onSaveTags={handleSaveTags}
                />
              </div>
            </header>

            <FailureDetailMeta
              createdAt={failure.created_at}
              lastRunDate={failure.last_run_date}
            />

            <FailureDetailSections
              failure={failure}
              cleanAttemptEvents={cleanAttemptEvents}
              cleanEventsLoading={cleanEventsLoading}
              onToggleCleanEvents={handleToggleCleanEvents}
              expandedAttempts={expandedAttempts}
              refineAttemptEvents={refineAttemptEvents}
              refineEventsLoading={refineEventsLoading}
              onToggleRefineEvents={handleToggleRefineEvents}
              expandedRefineAttempts={expandedRefineAttempts}
              rootCauseAttemptEvents={rootCauseAttemptEvents}
              rootCauseEventsLoading={rootCauseEventsLoading}
              onToggleRootCauseEvents={handleToggleRootCauseEvents}
              expandedRootCauseAttempts={expandedRootCauseAttempts}

              onRefreshStage={handleRefreshStage}
              refreshingStage={refreshingStage}
              onRefresh={fetchFailureData}


            />

            <FailureDetailResults results={failure.latest_results} />
          </>
        )}
      </main>
    </div>
  )
}

export default FailureDetailPage
